import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { ServerError, SessionExpiredError } from "../error/error";
import { CookieHelper } from "./CookieHelper";
import {
  SESSION_ENDED_REASON_COOKIE_KEY,
  SESSION_ENDED_REASON_EXPIRED,
} from "../constants/environment";

export type Method =
  | "get"
  | "GET"
  | "delete"
  | "DELETE"
  | "head"
  | "HEAD"
  | "options"
  | "OPTIONS"
  | "post"
  | "POST"
  | "put"
  | "PUT"
  | "patch"
  | "PATCH"
  | "link"
  | "LINK"
  | "unlink"
  | "UNLINK";

export type ResponseType =
  | "arraybuffer"
  | "blob"
  | "document"
  | "json"
  | "text"
  | "stream";

export default class HttpClient {
  interceptor: number = 0;
  cookieHelper: CookieHelper;

  constructor(cookieHelper: CookieHelper) {
    this.cookieHelper = cookieHelper;
  }

  authenticateRequests = (token: string) => {
    axios.interceptors.request.eject(this.interceptor);
    this.interceptor = axios.interceptors.request.use((request) => {
      request.headers["Authorization"] = `Bearer ${token}`;
      return request;
    });
  };

  unauthenticateRequests = () => {
    axios.interceptors.request.eject(this.interceptor);
    this.interceptor = 0;
  };

  request: <T>(
    url,
    {
      data,
      method,
      headers,
      factory,
    }: {
      data?: any;
      method?: Method;
      headers?: any;
      responseType?: ResponseType;
      factory?: (data, headers) => T;
    }
  ) => Promise<T> = (url, config) => {
    const _config: AxiosRequestConfig = {
      url,
      method: config.method || config.data ? "post" : "get",
      headers: {
        "Content-Type": "application/json",
        ...config.headers,
      },
      responseType: config.responseType,
      data: config.data,
    };

    return axios
      .request(_config)
      .catch((result: AxiosError) => {
        const status = result.response?.status;

        // 401 means the token could not be verified. 407
        // (SC_PROXY_AUTHENTICATION_REQUIRED) is pivot-health-authentication's
        // specific signal for an EXPIRED token (JwtRequestFilter.kt) -- not a
        // generic proxy error in this app's context, and previously unhandled,
        // so an expired token surfaced as an opaque ServerError.
        //
        // Both end the client session identically: from here they mean the
        // same thing, that the server has stopped honouring the token. This is
        // the fallback for a token that died before the idle clock caught it --
        // a sleep or clock jump longer than the idle window, or a request
        // racing an idle-logout that already fired in another tab.
        if ((status === 401 || status === 407) && this.interceptor > 0) {
          this.unauthenticateRequests();
          this.cookieHelper.setItem(
            SESSION_ENDED_REASON_COOKIE_KEY,
            SESSION_ENDED_REASON_EXPIRED,
            1
          );
          window.location.reload();
          throw new SessionExpiredError();
        }

        throw new ServerError(result.message, status);
      })
      .then(
        async (response) =>
          config.factory?.(await response.data, response.headers) ||
          (await response.data)
      );
  };
}
